import {useEffect, useRef, useState} from "react";
import {SEQUENCE_ITEM_STATUS, SEQUENCE_RUN_STATE} from "./Constants";

/**
 * Hook that processes items one after another, waiting intervalMs between two items
 * (e.g. sending one command per item of a test sequence).
 *
 * process(item, index) is awaited for every item. Resolving to false or rejecting marks the item FAILED and
 * pauses the run in the FAILED state, from where resume() retries that item.
 * pause() can be followed by resume(); stop() ends the run for good. An item already in flight when the run is
 * paused or stopped keeps its result. New items reset the run, and the run stops on unmount.
 *
 * @param items items to process in order; keep the reference stable (e.g. useMemo), a new reference resets the run
 * @param intervalMs wait between the end of one item and the start of the next one
 * @param process async function that processes one item
 * @param minInProgressMs minimum time an item stays IN_PROGRESS, so that it is visible to the user
 * @return {{statuses: string[], runState: string, isItemInProgress: boolean,
 *           start: function, pause: function, resume: function, stop: function}}
 *          statuses[index] is a SEQUENCE_ITEM_STATUS (empty until the first run), runState a SEQUENCE_RUN_STATE
 */
const useSequenceRunner = ({items, intervalMs, process, minInProgressMs = 0}) => {
    const [storedStatuses, setStatuses] = useState([]);
    const [storedRunState, setRunState] = useState(SEQUENCE_RUN_STATE.IDLE);
    // Items the stored state belongs to. When items change, the state is only reset by the effect after the
    // render, so for that render report a fresh IDLE run instead of the state of the previous items.
    const stateItemsRef = useRef(items);
    const isStateForOtherItems = stateItemsRef.current !== items;
    const statuses = isStateForOtherItems ? [] : storedStatuses;
    const runState = isStateForOtherItems ? SEQUENCE_RUN_STATE.IDLE : storedRunState;
    const runIdRef = useRef(0);
    // Changes when the statuses are reset, so that an item still in flight from before does not update them
    const statusesVersionRef = useRef(0);
    const isMountedRef = useRef(true);
    const pendingWaitRef = useRef(null);

    const isItemInProgress = statuses.includes(SEQUENCE_ITEM_STATUS.IN_PROGRESS);

    const wait = (ms) => new Promise(resolve => {
        const timer = setTimeout(() => {
            pendingWaitRef.current = null;
            resolve();
        }, Math.max(ms, 0));
        pendingWaitRef.current = {timer, resolve};
    });

    const cancelPendingWait = () => {
        if (pendingWaitRef.current) {
            clearTimeout(pendingWaitRef.current.timer);
            pendingWaitRef.current.resolve();
            pendingWaitRef.current = null;
        }
    };

    // A new run id makes the running loop exit at its next check
    const cancelRun = () => {
        runIdRef.current++;
        cancelPendingWait();
    };

    const updateStatus = (index, status) => {
        setStatuses(previous => {
            const next = [...previous];
            next[index] = status;
            return next;
        });
    };

    const run = async (fromIndex) => {
        cancelRun();
        const runId = runIdRef.current;
        const statusesVersion = statusesVersionRef.current;
        const isActive = () => isMountedRef.current && runIdRef.current === runId;
        setRunState(SEQUENCE_RUN_STATE.RUNNING);

        for (let index = fromIndex; index < items.length; index++) {
            updateStatus(index, SEQUENCE_ITEM_STATUS.IN_PROGRESS);
            const startedAt = Date.now();
            let isSuccess;
            try {
                isSuccess = (await process(items[index], index)) !== false;
            } catch (e) {
                isSuccess = false;
            }
            if (isSuccess && isActive()) {
                await wait(minInProgressMs - (Date.now() - startedAt));
            }
            if (!isMountedRef.current || statusesVersionRef.current !== statusesVersion) {
                return;
            }
            updateStatus(index, isSuccess ? SEQUENCE_ITEM_STATUS.COMPLETED : SEQUENCE_ITEM_STATUS.FAILED);
            if (!isSuccess) {
                // A failure after pause() or stop() keeps that run state
                if (isActive()) {
                    setRunState(SEQUENCE_RUN_STATE.FAILED);
                }
                return;
            }
            if (!isActive()) {
                return;
            }
            if (index < items.length - 1) {
                await wait(intervalMs);
                if (!isActive()) {
                    return;
                }
            }
        }
        // Still active here: the last item was checked above and is not followed by a wait
        setRunState(SEQUENCE_RUN_STATE.COMPLETED);
    };

    const resetStatuses = (newStatuses) => {
        statusesVersionRef.current++;
        setStatuses(newStatuses);
    };

    const start = () => {
        resetStatuses(items.map(() => SEQUENCE_ITEM_STATUS.PENDING));
        run(0);
    };

    const pause = () => {
        cancelRun();
        setRunState(SEQUENCE_RUN_STATE.PAUSED);
    };

    // Continues from the first item not COMPLETED; ignored while an item is still in flight to avoid sending it twice
    const resume = () => {
        const fromIndex = statuses.findIndex(status => status !== SEQUENCE_ITEM_STATUS.COMPLETED);
        if (isItemInProgress || fromIndex < 0) {
            return;
        }
        run(fromIndex);
    };

    const stop = () => {
        cancelRun();
        setRunState(SEQUENCE_RUN_STATE.STOPPED);
    };

    useEffect(() => {
        stateItemsRef.current = items;
        cancelRun();
        resetStatuses([]);
        setRunState(SEQUENCE_RUN_STATE.IDLE);
    }, [items]);

    useEffect(() => () => {
        isMountedRef.current = false;
        cancelRun();
    }, []);

    return {statuses, runState, isItemInProgress, start, pause, resume, stop};
};

export default useSequenceRunner;



// WEBPACK FOOTER //
// ./src/components/util/useSequenceRunner.js